import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, CheckCircle, FileText, ArrowRight, Shield, AlertTriangle, Beaker, Wind, Droplets, Scale, Eye } from 'lucide-react';

const AntimonyRoasting = () => {
  const servicePillars = [
    { 
      title: 'Synthetic Minor Permitting Strategy', 
      description: 'Strategic permit design to accept federally enforceable operational limits (ORLs). We help facilities stay below "Major Source" thresholds where possible, avoiding costly Title V administrative burdens while maintaining production targets.',
      icon: FileText
    },
    { 
      title: 'Arsenic & Heavy Metal Control', 
      description: 'Specialized oversight for the capture and control of volatile heavy metals. We develop monitoring plans for Arsenic and Lead (Pb) that protect worker safety and satisfy stringent NESHAP requirements.',
      icon: AlertTriangle
    },
    { 
      title: 'SO2 Scrubber & Acid Gas Management', 
      description: 'Technical defense for SO2 control systems. Whether using lime injection or wet scrubbing, we ensure your abatement technology is properly credited in your air permit to allow for variable sulfur feedstocks.',
      icon: Wind
    },
    { 
      title: 'Slag & Particulate Waste (RCRA)', 
      description: 'Integrated air-to-waste support. We help classify baghouse dust and roaster slag, navigating the intersection of air permits and RCRA hazardous waste disposal rules.',
      icon: Droplets
    },
    { 
      title: 'NESHAP & NSPS Compliance', 
      description: 'Navigating 40 CFR 63 Subpart EEEEEEE (Gold/Ore Processing) and other metallurgical standards. We ensure your Compliance Assurance Monitoring (CAM) plans are practical and audit-ready.',
      icon: Shield
    },
    { 
      title: 'Opacity & Visibility Defense', 
      description: 'Managing visible emissions in sensitive areas. We provide the technical data to defend against opacity violations during furnace startups, shutdowns, and soot-blowing events.',
      icon: Eye
    },
  ];

  return (
    <div className="min-h-screen" data-testid="antimony-roasting-page">
      {/* Hero Banner Section */}
      <section className="hero-section relative text-white overflow-hidden" style={{
        height: '60vh',
        maxHeight: '720px',
        minHeight: '600px'
      }}>
        <style>{`
          .hero-section picture {
            width: 100%;
            height: 100%;
            display: block;
          }
          
          .hero-section picture img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center bottom !important;
            display: block !important;
          }
          
          @media (max-width: 900px) {
            .hero-section {
              height: auto !important;
              max-height: none !important;
              min-height: 420px !important;
            }
            .hero-section picture img {
              object-position: center center !important;
            }
          }
        `}</style>
        
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          {/* Mobile Vertical (portrait phones) */}
          <source
            media="(max-width: 480px) and (orientation: portrait)"
            srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/t27g3rri_antimony_hero_mobile_vertical_960x1200.webp"
            type="image/webp"
          />

          {/* Mobile Horizontal */}
          <source
            media="(max-width: 768px)"
            srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/axh4220w_antimony_hero_mobile_horizontal_960x640.webp"
            type="image/webp"
          />

          {/* Tablet */}
          <source
            media="(max-width: 1200px)"
            srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/c24pff4t_antimony_hero_tablet_1536x900.webp"
            type="image/webp"
          />

          {/* Desktop default */}
          <img
            src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/rvgbm1sj_antimony_hero_desktop_2560x1200.webp"
            alt="Antimony roasting facility with thermal processing equipment and industrial stack emissions, used for regulatory air quality permitting and compliance in mining and mineral processing operations in Alaska"
            loading="eager"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }}
            className="w-full h-full"
          />
        </picture>
        
        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center py-20 md:py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold drop-shadow-lg mb-6">
              Strategic Compliance for Critical Mineral Processing
            </h1>
            <p className="text-2xl md:text-2xl max-w-3xl drop-shadow-md mb-8 leading-relaxed">
              Antimony roasting presents unique metallurgical and regulatory challenges. From Arsenic management to SO2 scrubbing, we provide the "Kiln-to-Stack" oversight needed to keep high-value mineral assets operational and compliant.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact/antimony-roasting"
                className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
              >
                Secure Your Processing Permit
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Chemistry of Compliance</h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              In pyrometallurgy, your ore quality dictates your regulatory liability. Antimony roasting is a high-temperature game where sulfur dioxide (SO2) loads and volatile heavy metals—specifically Arsenic—can threaten your operating permit.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Raven Environmental provides a "Furnace-to-Filter" strategy. We don't just file paperwork; we help you correlate furnace feed rates and temperatures with scrubber efficiency, ensuring you maximize production without tripping emission limits. We bridge the gap between the mine's geology and the EPA's standards.
            </p>
          </div>
        </div>
      </section>

      {/* Core Service Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Core Service Pillars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicePillars.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg border-t-4 border-amber-500">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mr-3">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Technical Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Box 1: Emissions of Concern */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-amber-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Emissions of Concern</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Sulfur Dioxide (SO2)</span>
                    <p className="text-sm text-gray-600">Scrubber Efficiency Modeling</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Arsenic (As)</span>
                    <p className="text-sm text-gray-600">Volatile Fraction Capture</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Particulate Matter (PM2.5)</span>
                    <p className="text-sm text-gray-600">Baghouse Integrity</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-gray-900">Antimony Trioxide (Sb2O3)</span>
                    <p className="text-sm text-gray-600">Product Recovery vs. Emission</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Box 2: Regulatory Framework */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-amber-500">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Regulatory Framework</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Title V vs. Synthetic Minor Applicability</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PSD (Prevention of Significant Deterioration) Avoidance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Alaska Air Quality Control (18 AAC 50)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Toxic Release Inventory (TRI) Reporting</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Importance */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategic Importance</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              As the US supply chain turns toward domestic critical minerals, Raven Environmental ensures that Alaska's processors can operate with the confidence of full regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Secure Your Processing Permit?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us for strategic compliance oversight that bridges metallurgy and regulation, ensuring your antimony roasting operation stays compliant and operational.
          </p>
          <Link
            to="/contact/antimony-roasting"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
          >
            Contact Our Team
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AntimonyRoasting;
